// ForgeTrack — AI Model Configuration
// Route: /ai-models

import { createFileRoute } from '@tanstack/react-router';
import React, { useState, useEffect, useCallback } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, { GlassPanel, SectionLabel, LoadingShimmer, StatusBadge } from '../components/engine/EnginePageShell';
import { useApiClient } from '@forgetrack/api-client';
import { useCurrentUser } from '../lib/api/useCurrentUser';

export const Route = createFileRoute('/ai-models')({
  component: AIModelConfigPage,
});

interface ModelOption {
  modelName: string;
  provider: string;
  tier: string;
  inputCostPerMTok: number;
  outputCostPerMTok: number;
}

interface Recommendation {
  taskType: string;
  taskDescription: string;
  recommendedTier: string;
  recommendedModel: string;
  recommendedProvider: string;
  inputCostPerMTok: number;
  outputCostPerMTok: number;
  rationale: string;
  alternatives: ModelOption[];
}

interface Assignment {
  id: number;
  taskType: string;
  modelName: string;
  provider: string;
  deploymentName: string | null;
  isAutoRecommended: boolean;
  overriddenAt: string | null;
}

interface HangfireJob {
  id: string;
  state: string;
  method: string;
  queue: string | null;
  createdAt: string | null;
  fetchedAt: string | null;
  stateChangedAt: string | null;
  reason: string | null;
}

interface HangfireServer {
  name: string;
  workers: number;
  queues: string[];
  heartbeat: string;
  startedAt: string;
}

interface HangfireQueue {
  name: string;
  length: number;
  fetched: number;
  firstJobs: string[];
}

interface CatalogRow {
  id: number;
  modelKey: string;
  displayName: string;
  provider: string;
  tier: string;
  rationale: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface DiagnosticsResponse {
  config: {
    endpointSet: boolean;
    endpointHost: string | null;
    apiKeySet: boolean;
    fallbackDeploymentName: string | null;
  };
  foundry: {
    listError: string | null;
    successfulApiVersion: string | null;
    usingFallback: boolean;
    deployments: Array<{ deploymentId: string; modelId: string; status: string }>;
  };
  promptLibrary: {
    allModelNamesInUse: string[];
    unmappedAzureOpenAIModels: string[];
  };
}

const TIER_COLORS: Record<string, string> = {
  economy: C.neon,
  standard: C.cyan,
  premium: C.violet,
};

const TIER_LABELS: Record<string, string> = {
  economy: '💰 Economy',
  standard: '⚡ Standard',
  premium: '🧠 Premium',
};

const PROVIDER_LABELS: Record<string, string> = {
  azure_ai: 'Azure AI',
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
};

function AIModelConfigPage() {
  const { isAdmin, loading: userLoading } = useCurrentUser();
  const { createApi } = useApiClient();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [diag, setDiag] = useState<DiagnosticsResponse | null>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState<string | null>(null);
  const [refreshingDeployments, setRefreshingDeployments] = useState(false);
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [hangfireJobs, setHangfireJobs] = useState<HangfireJob[]>([]);
  const [hangfireServers, setHangfireServers] = useState<HangfireServer[]>([]);
  const [hangfireQueues, setHangfireQueues] = useState<HangfireQueue[]>([]);
  const [hangfireLoading, setHangfireLoading] = useState(false);
  const [killingJobId, setKillingJobId] = useState<string | null>(null);

  const runDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    setDiagError(null);
    try {
      const api = await createApi();
      const { data } = await api.get<DiagnosticsResponse>('/api/v1/admin/diagnostics/ai-config');
      setDiag(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Diagnostic call failed';
      setDiagError(msg);
    } finally {
      setDiagLoading(false);
    }
  }, [createApi]);

  const refreshDeployments = useCallback(async () => {
    setRefreshingDeployments(true);
    try {
      const api = await createApi();
      await api.post('/api/v1/admin/diagnostics/ai-config/refresh-deployments');
      await runDiagnostics();
    } finally {
      setRefreshingDeployments(false);
    }
  }, [createApi, runDiagnostics]);

  const [refreshingCatalog, setRefreshingCatalog] = useState(false);
  const refreshCatalogEmbeddings = useCallback(async () => {
    if (!window.confirm(
      'Refresh embeddings for every work item in this tenant? Idempotent — ' +
      'rows already on the current embedding model are skipped unless force=true.',
    )) return;
    setRefreshingCatalog(true);
    try {
      const api = await createApi();
      const res = await api.post<{ jobId: string; tenantId: number }>(
        '/api/v1/import/refresh-catalog-embeddings');
      window.alert(`Catalog embedding refresh enqueued. Job ID: ${res.data.jobId}\nTakes 30–60s for ~500 work items.`);
    } catch (err: any) {
      const detail = err?.response?.data?.error ?? err?.message ?? 'Refresh failed.';
      window.alert(`⚠ ${detail}`);
    } finally {
      setRefreshingCatalog(false);
    }
  }, [createApi]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = await createApi();
      const [recsRes, assignRes] = await Promise.all([
        api.get('/api/v1/ai-models/recommendations'),
        api.get('/api/v1/ai-models/assignments'),
      ]);
      setRecommendations(recsRes.data ?? []);
      setAssignments(assignRes.data ?? []);
    } catch (err) {
      console.error('Failed to load AI model config', err);
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const api = await createApi();
      const { data } = await api.get<CatalogRow[]>('/api/v1/ai-models/catalog');
      setCatalog(data ?? []);
    } catch (err) {
      console.error('Failed to load model catalog', err);
    } finally {
      setCatalogLoading(false);
    }
  }, [createApi]);

  const upsertCatalog = useCallback(async (row: Partial<CatalogRow>) => {
    const api = await createApi();
    await api.post('/api/v1/ai-models/catalog', {
      modelKey:    row.modelKey,
      displayName: row.displayName,
      provider:    row.provider,
      tier:        row.tier,
      rationale:   row.rationale,
      sortOrder:   row.sortOrder,
      isActive:    row.isActive,
    });
    await loadCatalog();
    await load();
  }, [createApi, loadCatalog, load]);

  const deactivateCatalog = useCallback(async (id: number) => {
    if (!window.confirm('Deactivate this model? Hidden from picker; row preserved for audit.'))
      return;
    const api = await createApi();
    await api.post(`/api/v1/ai-models/catalog/${id}/deactivate`);
    await loadCatalog();
    await load();
  }, [createApi, loadCatalog, load]);

  const loadHangfire = useCallback(async () => {
    setHangfireLoading(true);
    try {
      const api = await createApi();
      const [jobsRes, serversRes] = await Promise.all([
        api.get<{ jobs: HangfireJob[] }>('/api/v1/admin/hangfire/jobs?state=Processing&take=100'),
        api.get<{ servers: HangfireServer[]; queues: HangfireQueue[] }>('/api/v1/admin/hangfire/servers'),
      ]);
      setHangfireJobs(jobsRes.data?.jobs ?? []);
      setHangfireServers(serversRes.data?.servers ?? []);
      setHangfireQueues(serversRes.data?.queues ?? []);
    } catch (err) {
      console.error('Failed to load Hangfire admin data', err);
    } finally {
      setHangfireLoading(false);
    }
  }, [createApi]);

  const killHangfireJob = useCallback(async (id: string) => {
    if (!window.confirm(
      `Delete Hangfire job ${id}? Sets state to Deleted; running worker finishes ` +
      'its current iteration and exits. Conversation classifications partially ' +
      'completed will leave Pending rows in DB — re-run classification to resume.',
    )) return;
    setKillingJobId(id);
    try {
      const api = await createApi();
      await api.post(`/api/v1/admin/hangfire/jobs/${id}/delete`);
      await loadHangfire();
    } catch (err) {
      console.error('Failed to kill Hangfire job', err);
      window.alert('Delete failed; see console.');
    } finally {
      setKillingJobId(null);
    }
  }, [createApi, loadHangfire]);

  useEffect(() => { load(); loadCatalog(); loadHangfire(); }, [load, loadCatalog, loadHangfire]);

  const setModel = async (taskType: string, model: ModelOption) => {
    setSaving(taskType);
    try {
      const api = await createApi();
      await api.put('/api/v1/ai-models/assignments', {
        taskType,
        modelName: model.modelName,
        provider: model.provider,
      });
      load();
    } finally {
      setSaving(null);
    }
  };

  const applyAll = async () => {
    setApplying(true);
    try {
      const api = await createApi();
      await api.post('/api/v1/ai-models/apply-recommendations');
      load();
    } finally {
      setApplying(false);
    }
  };

  const getAssignment = (taskType: string) => assignments.find(a => a.taskType === taskType);

  // Estimate monthly cost based on rough token counts per task
  const MONTHLY_TOKENS: Record<string, { input: number; output: number }> = {
    Classification: { input: 50_000_000, output: 5_000_000 },      // 163 convos × ~300K tokens
    EffortEstimation: { input: 10_000_000, output: 2_000_000 },
    RDNarrative: { input: 20_000_000, output: 10_000_000 },
    Summarization: { input: 30_000_000, output: 5_000_000 },
    ComplexityScoring: { input: 15_000_000, output: 3_000_000 },
  };

  const estimateMonthlyCost = (taskType: string, inputPerMTok: number, outputPerMTok: number) => {
    const t = MONTHLY_TOKENS[taskType] ?? { input: 10_000_000, output: 2_000_000 };
    return (t.input / 1_000_000 * inputPerMTok) + (t.output / 1_000_000 * outputPerMTok);
  };

  const totalMonthlyCost = recommendations.reduce((sum, rec) => {
    const assignment = getAssignment(rec.taskType);
    const model = assignment
      ? rec.alternatives.find(a => a.modelName === assignment.modelName)
      : rec.alternatives.find(a => a.modelName === rec.recommendedModel);
    if (!model) return sum;
    return sum + estimateMonthlyCost(rec.taskType, model.inputCostPerMTok, model.outputCostPerMTok);
  }, 0);

  if (!userLoading && !isAdmin) {
    return (
      <EnginePageShell title="AI Model Configuration" icon="🧠" accentColor={C.violet}>
        <GlassPanel accent={C.coral} style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 8 }}>Admin Access Required</div>
          <div style={{ fontSize: 13, color: C.muted }}>
            AI model configuration requires an Owner or Admin role. Contact your account administrator.
          </div>
        </GlassPanel>
      </EnginePageShell>
    );
  }

  return (
    <EnginePageShell title="AI Model Configuration" icon="🧠" accentColor={C.violet}>
      {loading && <LoadingShimmer />}

      {!loading && (
        <>
          {/* Diagnostics panel — surface AI config / Foundry / prompt-library mapping issues */}
          <GlassPanel accent={diag?.foundry.listError || (diag?.promptLibrary.unmappedAzureOpenAIModels.length ?? 0) > 0 ? C.coral : C.cyan} style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: diag ? 12 : 0 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F.display, marginBottom: 2 }}>
                  AI Provider Diagnostics
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>
                  Verifies KV config reaches the API, lists Foundry deployments, and flags prompts that reference missing models.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {diag && (
                  <button
                    onClick={refreshDeployments}
                    disabled={refreshingDeployments}
                    style={{
                      padding: '8px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                      fontFamily: F.mono, cursor: refreshingDeployments ? 'wait' : 'pointer',
                      background: C.surface, color: C.text, border: `1px solid ${C.faint}`,
                    }}
                  >
                    {refreshingDeployments ? '◌ Refreshing...' : 'Refresh Foundry Cache'}
                  </button>
                )}
                <button
                  onClick={refreshCatalogEmbeddings}
                  disabled={refreshingCatalog}
                  style={{
                    padding: '8px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    fontFamily: F.mono, cursor: refreshingCatalog ? 'wait' : 'pointer',
                    background: C.surface, color: C.neon, border: `1px solid ${C.neon}`,
                  }}
                  title="Re-embed all work items so Tier 1 cosine matching has cached vectors to compare against"
                >
                  {refreshingCatalog ? '◌ Enqueueing...' : '↻ Refresh Catalog Embeddings'}
                </button>
                <button
                  onClick={runDiagnostics}
                  disabled={diagLoading}
                  style={{
                    padding: '8px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    fontFamily: F.mono, cursor: diagLoading ? 'wait' : 'pointer',
                    background: diagLoading ? C.faint : C.violet, color: diagLoading ? C.dim : C.bg, border: 'none',
                  }}
                >
                  {diagLoading ? '◌ Running...' : diag ? '↻ Re-run' : '▶ Run Diagnostics'}
                </button>
              </div>
            </div>

            {diagError && (
              <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: `${C.coral}15`, color: C.coral, fontSize: 11, fontFamily: F.mono }}>
                {diagError}
              </div>
            )}

            {diag && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {/* Config column */}
                <div style={{ padding: 12, borderRadius: 6, background: C.surface, border: `1px solid ${C.faint}` }}>
                  <SectionLabel color={C.dim}>Config (from Key Vault)</SectionLabel>
                  <DiagRow label="Endpoint" value={diag.config.endpointSet ? (diag.config.endpointHost ?? 'set') : 'MISSING'} ok={diag.config.endpointSet} />
                  <DiagRow label="API Key" value={diag.config.apiKeySet ? 'set' : 'MISSING'} ok={diag.config.apiKeySet} />
                  <DiagRow label="Fallback Deployment" value={diag.config.fallbackDeploymentName ?? '(none)'} ok={!!diag.config.fallbackDeploymentName} />
                </div>

                {/* Foundry column */}
                <div style={{ padding: 12, borderRadius: 6, background: C.surface, border: `1px solid ${C.faint}` }}>
                  <SectionLabel color={C.dim}>Foundry Deployments</SectionLabel>
                  {diag.foundry.successfulApiVersion && (
                    <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginBottom: 6 }}>
                      via api-version {diag.foundry.successfulApiVersion}
                    </div>
                  )}
                  {diag.foundry.usingFallback && (
                    <div style={{ fontSize: 11, color: C.amber, fontFamily: F.mono, marginBottom: 6 }}>
                      ⚠ Listing failed — using configured fallback deployment for all routing
                    </div>
                  )}
                  {diag.foundry.deployments.length === 0 ? (
                    <div style={{ fontSize: 11, color: C.amber, fontFamily: F.mono }}>
                      ⚠ No deployments returned
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {diag.foundry.deployments.map(d => (
                        <div key={d.deploymentId} style={{ fontSize: 11, fontFamily: F.mono, color: C.text }}>
                          <span style={{ color: C.cyan }}>{d.deploymentId}</span>
                          <span style={{ color: C.dim }}> → {d.modelId}</span>
                          {d.status !== 'succeeded' && <span style={{ color: C.amber, marginLeft: 6 }}>[{d.status}]</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {diag.foundry.listError && (
                    <div style={{ marginTop: 8, fontSize: 10, color: C.coral, fontFamily: F.mono, wordBreak: 'break-word' }}>
                      Last error: {diag.foundry.listError}
                    </div>
                  )}
                </div>

                {/* Prompt library column */}
                <div style={{ padding: 12, borderRadius: 6, background: C.surface, border: `1px solid ${C.faint}` }}>
                  <SectionLabel color={C.dim}>Prompt Library</SectionLabel>
                  <div style={{ fontSize: 11, fontFamily: F.mono, color: C.text, marginBottom: 6 }}>
                    Models in use: <span style={{ color: C.cyan }}>{diag.promptLibrary.allModelNamesInUse.length}</span>
                  </div>
                  {diag.promptLibrary.unmappedAzureOpenAIModels.length > 0 ? (
                    <>
                      <div style={{ fontSize: 11, color: C.coral, fontFamily: F.mono, fontWeight: 600, marginBottom: 4 }}>
                        ✗ {diag.promptLibrary.unmappedAzureOpenAIModels.length} unmapped:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {diag.promptLibrary.unmappedAzureOpenAIModels.map(m => (
                          <div key={m} style={{ fontSize: 11, fontFamily: F.mono, color: C.coral }}>
                            • {m}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 11, color: C.neon, fontFamily: F.mono }}>
                      ✓ All Azure OpenAI prompts map to a deployment
                    </div>
                  )}
                </div>
              </div>
            )}
          </GlassPanel>

          {/* Header stats + Apply All */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: C.muted, fontFamily: F.display }}>
                Estimated monthly AI cost
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.neon, fontFamily: F.mono }}>
                ${totalMonthlyCost.toFixed(2)}
              </div>
            </div>
            <button
              onClick={applyAll}
              disabled={applying}
              style={{
                padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                fontFamily: F.mono, cursor: 'pointer',
                background: applying ? C.faint : `linear-gradient(135deg, ${C.violet}, ${C.cyan})`,
                color: applying ? C.dim : C.bg, border: 'none',
              }}
            >
              {applying ? '◌ Applying...' : '✨ Apply All Recommendations'}
            </button>
          </div>

          {/* Task cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {recommendations.map((rec) => {
              const assignment = getAssignment(rec.taskType);
              const currentModel = assignment?.modelName ?? rec.recommendedModel;
              const currentOption = rec.alternatives.find(a => a.modelName === currentModel);
              const isExpanded = expandedTask === rec.taskType;
              const isOverridden = assignment && !assignment.isAutoRecommended;
              const monthlyCost = currentOption
                ? estimateMonthlyCost(rec.taskType, currentOption.inputCostPerMTok, currentOption.outputCostPerMTok)
                : 0;

              return (
                <GlassPanel
                  key={rec.taskType}
                  accent={TIER_COLORS[currentOption?.tier ?? 'standard'] ?? C.cyan}
                  style={{ padding: 20 }}
                >
                  {/* Task header */}
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
                    onClick={() => setExpandedTask(isExpanded ? null : rec.taskType)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: F.display }}>
                          {rec.taskType}
                        </span>
                        <StatusBadge
                          color={TIER_COLORS[currentOption?.tier ?? 'standard'] ?? C.cyan}
                          label={TIER_LABELS[currentOption?.tier ?? 'standard'] ?? currentOption?.tier ?? ''}
                        />
                        {isOverridden && (
                          <span style={{ fontSize: 9, color: C.amber, fontFamily: F.mono }}>CUSTOM</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>
                        {rec.taskDescription}
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 11, fontFamily: F.mono }}>
                        <span style={{ color: C.text }}>
                          Model: <span style={{ color: C.cyan }}>{currentModel}</span>
                        </span>
                        <span style={{ color: C.dim }}>
                          {PROVIDER_LABELS[currentOption?.provider ?? ''] ?? currentOption?.provider}
                        </span>
                        <span style={{ color: C.neon }}>
                          ~${monthlyCost.toFixed(2)}/mo
                        </span>
                      </div>
                    </div>
                    <span style={{ fontSize: 14, color: C.dim }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>

                  {/* Recommendation rationale */}
                  {!isExpanded && (
                    <div style={{ marginTop: 8, fontSize: 11, color: C.dim, fontStyle: 'italic' }}>
                      💡 {rec.rationale}
                    </div>
                  )}

                  {/* Expanded: model selection */}
                  {isExpanded && (
                    <div style={{ marginTop: 16, borderTop: `1px solid ${C.faint}`, paddingTop: 16 }}>
                      <SectionLabel color={C.dim}>Choose Model</SectionLabel>

                      {/* Tier groups */}
                      {['economy', 'standard', 'premium'].map(tier => {
                        const tierModels = rec.alternatives.filter(a => a.tier === tier);
                        if (tierModels.length === 0) return null;

                        return (
                          <div key={tier} style={{ marginBottom: 12 }}>
                            <div style={{
                              fontSize: 10, fontWeight: 700, color: TIER_COLORS[tier], fontFamily: F.mono,
                              letterSpacing: '0.1em', marginBottom: 6,
                            }}>
                              {TIER_LABELS[tier]}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {tierModels.map(model => {
                                const isSelected = model.modelName === currentModel;
                                const isRecommended = model.modelName === rec.recommendedModel;
                                const cost = estimateMonthlyCost(rec.taskType, model.inputCostPerMTok, model.outputCostPerMTok);

                                return (
                                  <button
                                    key={model.modelName}
                                    onClick={(e) => { e.stopPropagation(); setModel(rec.taskType, model); }}
                                    disabled={saving === rec.taskType}
                                    style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                      padding: '10px 14px', borderRadius: 8,
                                      background: isSelected ? `${TIER_COLORS[tier]}15` : C.surface,
                                      border: `1px solid ${isSelected ? TIER_COLORS[tier] + '50' : C.faint}`,
                                      color: C.text, cursor: 'pointer', textAlign: 'left',
                                      fontFamily: F.display, fontSize: 12, width: '100%',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <span style={{
                                        width: 16, height: 16, borderRadius: '50%',
                                        border: `2px solid ${isSelected ? TIER_COLORS[tier] : C.faint}`,
                                        background: isSelected ? TIER_COLORS[tier] : 'none',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 10, color: C.bg,
                                      }}>
                                        {isSelected ? '✓' : ''}
                                      </span>
                                      <span style={{ fontWeight: isSelected ? 600 : 400 }}>
                                        {model.modelName}
                                      </span>
                                      {isRecommended && (
                                        <span style={{
                                          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                          background: `${C.neon}15`, color: C.neon,
                                        }}>
                                          RECOMMENDED
                                        </span>
                                      )}
                                      <span style={{ fontSize: 10, color: C.dim }}>
                                        {PROVIDER_LABELS[model.provider] ?? model.provider}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 12, fontSize: 10, fontFamily: F.mono }}>
                                      <span style={{ color: C.muted }}>
                                        ${model.inputCostPerMTok.toFixed(2)}/MTok in
                                      </span>
                                      <span style={{ color: C.muted }}>
                                        ${model.outputCostPerMTok.toFixed(2)}/MTok out
                                      </span>
                                      <span style={{ color: C.neon, fontWeight: 600 }}>
                                        ~${cost.toFixed(2)}/mo
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </GlassPanel>
              );
            })}
          </div>

          {/* Hangfire admin — surface stuck Processing jobs at top so
              operators see them immediately without scrolling */}
          <HangfireAdminPanel
            jobs={hangfireJobs}
            servers={hangfireServers}
            queues={hangfireQueues}
            loading={hangfireLoading}
            killingJobId={killingJobId}
            onRefresh={loadHangfire}
            onKill={killHangfireJob}
          />

          {/* Catalog admin */}
          <ModelCatalogPanel
            rows={catalog}
            loading={catalogLoading}
            onUpsert={upsertCatalog}
            onDeactivate={deactivateCatalog}
            onRefresh={loadCatalog}
          />
        </>
      )}
    </EnginePageShell>
  );
}

function DiagRow({ label, value, ok }: { label: string; value: string; ok: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, fontFamily: F.mono, marginBottom: 4 }}>
      <span style={{ color: C.muted }}>{label}</span>
      <span style={{ color: ok ? C.text : C.coral, fontWeight: ok ? 400 : 600, wordBreak: 'break-all', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ─── Model Catalog admin panel ───────────────────────────────────────

const PROVIDER_OPTS = ['azure_ai', 'anthropic', 'openai'] as const;
const TIER_OPTS     = ['economy', 'standard', 'premium'] as const;

function ModelCatalogPanel({
  rows, loading, onUpsert, onDeactivate, onRefresh,
}: {
  rows: CatalogRow[];
  loading: boolean;
  onUpsert: (row: Partial<CatalogRow>) => Promise<void>;
  onDeactivate: (id: number) => Promise<void>;
  onRefresh: () => void;
}) {
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<Partial<CatalogRow>>({});
  const [busy, setBusy] = useState(false);

  const startEdit = (row: CatalogRow) => {
    setEditingId(row.id);
    setDraft({ ...row });
  };
  const startNew = () => {
    setEditingId('new');
    setDraft({
      modelKey: '', displayName: '', provider: 'azure_ai',
      tier: 'standard', rationale: '', sortOrder: 100, isActive: true,
    });
  };
  const cancel = () => { setEditingId(null); setDraft({}); };

  const save = async () => {
    if (!draft.modelKey?.trim() || !draft.displayName?.trim()) {
      window.alert('Model key and display name are required.');
      return;
    }
    setBusy(true);
    try {
      await onUpsert(draft);
      cancel();
    } catch (err: any) {
      const detail = err?.response?.data?.error ?? err?.message ?? 'Save failed.';
      window.alert(`⚠ ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassPanel style={{ marginTop: 24 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', padding: '12px 16px 0',
      }}>
        <SectionLabel>MODEL CATALOG · {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}</SectionLabel>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: F.mono,
              background: 'transparent', color: C.dim, border: `1px solid ${C.faint}`,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? '◌' : '↻'} Refresh
          </button>
          {editingId !== 'new' && (
            <button
              onClick={startNew}
              style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: F.mono,
                background: 'transparent', color: C.neon, border: `1px solid ${C.neon}`,
                cursor: 'pointer',
              }}
            >
              + Add model
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ fontSize: 12, color: C.dim, fontFamily: F.display, margin: '8px 0 12px' }}>
          The picker above reads from this catalog. Add a row to make a new model
          available system-wide. Pricing is read separately from MonetaryRates
          (LiteLLM sync) — a row only appears in the picker when its ModelKey has
          matching pricing data.
        </div>

        {editingId === 'new' && (
          <CatalogEditRow draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} busy={busy} isNew />
        )}

        <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: C.dim, textAlign: 'left' }}>
              <th style={{ padding: '4px 8px' }}>Model key</th>
              <th style={{ padding: '4px 8px' }}>Display</th>
              <th style={{ padding: '4px 8px' }}>Provider</th>
              <th style={{ padding: '4px 8px' }}>Tier</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Sort</th>
              <th style={{ padding: '4px 8px' }}>Active</th>
              <th style={{ padding: '4px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              editingId === r.id ? (
                <tr key={r.id}>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <CatalogEditRow draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} busy={busy} />
                  </td>
                </tr>
              ) : (
                <tr key={r.id} style={{ borderTop: `1px solid ${C.faint}`, opacity: r.isActive ? 1 : 0.45 }}>
                  <td style={{ padding: '4px 8px', color: C.text }}>{r.modelKey}</td>
                  <td style={{ padding: '4px 8px', color: C.muted }}>{r.displayName}</td>
                  <td style={{ padding: '4px 8px', color: C.dim }}>{PROVIDER_LABELS[r.provider] ?? r.provider}</td>
                  <td style={{ padding: '4px 8px',
                               color: TIER_COLORS[r.tier] ?? C.dim }}>
                    {TIER_LABELS[r.tier] ?? r.tier}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: C.dim }}>{r.sortOrder}</td>
                  <td style={{ padding: '4px 8px',
                               color: r.isActive ? C.neon : C.dim }}>
                    {r.isActive ? '✓' : '—'}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => startEdit(r)}
                      style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 10, fontFamily: F.mono,
                        background: 'transparent', color: C.cyan, border: `1px solid ${C.cyan}`,
                        cursor: 'pointer', marginRight: 4,
                      }}
                    >
                      Edit
                    </button>
                    {r.isActive && (
                      <button
                        onClick={() => onDeactivate(r.id)}
                        style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 10, fontFamily: F.mono,
                          background: 'transparent', color: C.coral, border: `1px solid ${C.coral}`,
                          cursor: 'pointer',
                        }}
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

function CatalogEditRow({
  draft, setDraft, onSave, onCancel, busy, isNew,
}: {
  draft: Partial<CatalogRow>;
  setDraft: (d: Partial<CatalogRow>) => void;
  onSave: () => void;
  onCancel: () => void;
  busy: boolean;
  isNew?: boolean;
}) {
  const upd = (patch: Partial<CatalogRow>) => setDraft({ ...draft, ...patch });
  return (
    <div style={{
      display: 'grid', gap: 8, padding: 10, marginBottom: 10,
      gridTemplateColumns: '1fr 1fr 110px 110px 70px 90px',
      alignItems: 'end',
      background: C.surface, border: `1px solid ${C.violet}`, borderRadius: 6,
    }}>
      <Field label={isNew ? 'Model key (e.g. gpt-5.5)' : 'Model key'}>
        <input
          type="text" value={draft.modelKey ?? ''}
          onChange={e => upd({ modelKey: e.target.value })}
          disabled={!isNew}
          style={inputStyle}
        />
      </Field>
      <Field label="Display name">
        <input
          type="text" value={draft.displayName ?? ''}
          onChange={e => upd({ displayName: e.target.value })}
          style={inputStyle}
        />
      </Field>
      <Field label="Provider">
        <select
          value={draft.provider ?? 'azure_ai'}
          onChange={e => upd({ provider: e.target.value })}
          style={inputStyle}
        >
          {PROVIDER_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </Field>
      <Field label="Tier">
        <select
          value={draft.tier ?? 'standard'}
          onChange={e => upd({ tier: e.target.value })}
          style={inputStyle}
        >
          {TIER_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Sort">
        <input
          type="number" value={draft.sortOrder ?? 100}
          onChange={e => upd({ sortOrder: parseInt(e.target.value || '100', 10) })}
          style={inputStyle}
        />
      </Field>
      <Field label="Active">
        <input
          type="checkbox" checked={draft.isActive ?? true}
          onChange={e => upd({ isActive: e.target.checked })}
          style={{ marginTop: 6 }}
        />
      </Field>
      <div style={{ gridColumn: '1 / -1' }}>
        <Field label="Rationale (one line shown in picker)">
          <input
            type="text" value={draft.rationale ?? ''}
            onChange={e => upd({ rationale: e.target.value })}
            style={inputStyle}
          />
        </Field>
      </div>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel} disabled={busy}
          style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: F.mono,
            background: 'transparent', color: C.dim, border: `1px solid ${C.faint}`,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onSave} disabled={busy}
          style={{
            padding: '4px 12px', borderRadius: 4, fontSize: 11, fontFamily: F.mono,
            background: 'transparent', color: C.neon, border: `1px solid ${C.neon}`,
            cursor: busy ? 'wait' : 'pointer', fontWeight: 600,
          }}
        >
          {busy ? '◌ Saving...' : isNew ? 'Create' : 'Save'}
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '4px 6px',
  border: `1px solid ${C.faint}`, borderRadius: 4,
  background: C.bg, color: C.text,
  fontFamily: F.mono, fontSize: 11,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>{label}</span>
      {children}
    </label>
  );
}

// ─── Hangfire admin panel ────────────────────────────────────────────

function HangfireAdminPanel({
  jobs, servers, queues, loading, killingJobId, onRefresh, onKill,
}: {
  jobs: HangfireJob[];
  servers: HangfireServer[];
  queues: HangfireQueue[];
  loading: boolean;
  killingJobId: string | null;
  onRefresh: () => void;
  onKill: (id: string) => void;
}) {
  const fmt = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = Date.now();
    const elapsed = Math.round((now - d.getTime()) / 1000);
    if (elapsed < 60) return `${elapsed}s ago`;
    if (elapsed < 3600) return `${Math.round(elapsed / 60)}m ago`;
    return `${Math.round(elapsed / 3600)}h ago`;
  };

  const stuckCount = jobs.filter(j => {
    if (!j.fetchedAt) return false;
    return (Date.now() - new Date(j.fetchedAt).getTime()) > 30 * 60 * 1000;
  }).length;

  return (
    <GlassPanel
      accent={stuckCount > 0 ? C.coral : C.cyan}
      style={{ marginTop: 24 }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', padding: '12px 16px 0',
      }}>
        <SectionLabel color={stuckCount > 0 ? C.coral : C.cyan}>
          HANGFIRE · {jobs.length} processing
          {stuckCount > 0 && ` · ${stuckCount} stuck >30m`}
        </SectionLabel>
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: F.mono,
            background: 'transparent', color: C.dim, border: `1px solid ${C.faint}`,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? '◌' : '↻'} Refresh
        </button>
      </div>
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ fontSize: 12, color: C.dim, fontFamily: F.display, margin: '8px 0 12px' }}>
          Background job worker status. Long-running classifications can hold a
          worker indefinitely — Delete here to free workers without waiting for
          them to finish naturally. Conversations partway through stay Pending
          and resume on the next classify run.
        </div>

        {/* Servers + queues summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
          <div style={{ padding: 10, borderRadius: 6, background: C.surface, border: `1px solid ${C.faint}` }}>
            <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginBottom: 6 }}>
              SERVERS
            </div>
            {servers.length === 0 ? (
              <div style={{ fontSize: 11, color: C.coral, fontFamily: F.mono }}>
                ⚠ No active server — no jobs will run
              </div>
            ) : (
              servers.map(s => (
                <div key={s.name} style={{ fontSize: 11, color: C.text, fontFamily: F.mono, marginBottom: 4 }}>
                  <div style={{ color: C.cyan }}>{s.workers}× workers</div>
                  <div style={{ color: C.dim, fontSize: 10 }}>
                    heartbeat {fmt(s.heartbeat)} · started {fmt(s.startedAt)}
                  </div>
                </div>
              ))
            )}
          </div>
          <div style={{ padding: 10, borderRadius: 6, background: C.surface, border: `1px solid ${C.faint}` }}>
            <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginBottom: 6 }}>
              QUEUE DEPTHS
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {queues.map(q => (
                <div key={q.name} style={{
                  fontSize: 11, fontFamily: F.mono,
                  padding: '2px 8px', borderRadius: 4,
                  border: `1px solid ${q.length > 0 ? C.amber : C.faint}`,
                  color: q.length > 0 ? C.amber : C.dim,
                }}>
                  {q.name}: {q.length}
                  {q.fetched > 0 && <span style={{ color: C.cyan }}> · {q.fetched} active</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {jobs.length === 0 ? (
          <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, padding: 12, textAlign: 'center' }}>
            No jobs currently processing.
          </div>
        ) : (
          <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: C.dim, textAlign: 'left' }}>
                <th style={{ padding: '4px 8px' }}>Job ID</th>
                <th style={{ padding: '4px 8px' }}>Method</th>
                <th style={{ padding: '4px 8px' }}>Started</th>
                <th style={{ padding: '4px 8px' }}>Server</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => {
                const startedMs = j.fetchedAt ? Date.now() - new Date(j.fetchedAt).getTime() : 0;
                const stuck = startedMs > 30 * 60 * 1000;
                return (
                  <tr
                    key={j.id}
                    style={{
                      borderTop: `1px solid ${C.faint}`,
                      background: stuck ? `${C.coral}08` : 'transparent',
                    }}
                  >
                    <td style={{ padding: '4px 8px', color: C.text }}>#{j.id}</td>
                    <td style={{ padding: '4px 8px', color: C.muted }}>{j.method}</td>
                    <td style={{ padding: '4px 8px', color: stuck ? C.coral : C.cyan }}>
                      {fmt(j.fetchedAt)}
                      {stuck && <span style={{ marginLeft: 6 }}>⚠ stuck</span>}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.dim, fontSize: 10 }}>
                      {j.reason ?? '—'}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <button
                        onClick={() => onKill(j.id)}
                        disabled={killingJobId === j.id}
                        style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 10, fontFamily: F.mono,
                          background: 'transparent', color: C.coral, border: `1px solid ${C.coral}`,
                          cursor: killingJobId === j.id ? 'wait' : 'pointer',
                          opacity: killingJobId === j.id ? 0.5 : 1,
                        }}
                      >
                        {killingJobId === j.id ? '◌ Killing...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </GlassPanel>
  );
}
